import 'bootstrap/dist/css/bootstrap.css';
import './style.css';
import * as bootstrap from 'bootstrap';
// Configure HuggingFace env early to prevent relative URL issues
import { env as hfEnv } from '@huggingface/transformers';
hfEnv.allowLocalModels = false;
hfEnv.useBrowserCache = true;
hfEnv.remoteHost = 'https://huggingface.co';
import { appState } from './appState';
import { handleMainAction, handleClear, handleCopy } from './anonymization';
import { bootstrapAuthSession, handleLogout, handleSocialLogin, handleUpgrade, initializeEncryptionForUser } from './auth';
import { handleAddByokKey, initModelDropdown, loadByokModelCatalog, renderByokKeys, renderByokProviders, renderModelDropdown, updateByokProviderModels } from './byok';
import { closeSidebarOffcanvas, loadChat, renderChats, renderProjects, resetProjectSelection, sendMessageToAI } from './chat';
import { handleDeleteConfirm, registerDeleteHandlers } from './deleteModal';
import { restartDemoLoop, stopDemoLoop } from './demo';
import { dom, initDomElements } from './dom';
import { clearAllFiles, handleDrop, handleFileSelect } from './files';
import { deleteChatMemory, deleteProjectMemory } from './memory';
import { initializePipeline } from './pipeline';
import { initializeI18n, initializeLanguageSelect, t } from './i18n';
import { clearProjectSettings, handleGlobalMemoryScopeChange, handleGlobalMemoryToggle, handleGlobalPromptChange, handleProjectMemoryScopeChange, handleProjectMemoryToggle, handleProjectPromptChange, handleProjectUseGlobalToggle, loadGlobalSettings, refreshProjectSettingsContext, renderSettingsModal } from './settings';
import { storage } from './storage';
import { store } from './store';
import { setAppState, toggleTheme, updateUI } from './ui';
import { AppState } from './types';
const AUTO_REVIEW_KEY_PREFIX = 'blindquery_auto_review_';
async function syncAutoReviewPreference() {
    if (!dom.autoReviewCheckbox) {
        return;
    }
    // Auto-review works for both logged in and anonymous users
    const user = store.getUser();
    const userId = user?.id ?? 'anonymous';
    dom.autoReviewCheckbox.disabled = false;
    const storedPreference = await storage.load(`${AUTO_REVIEW_KEY_PREFIX}${userId}`);
    appState.autoReviewEnabled = Boolean(storedPreference);
    dom.autoReviewCheckbox.checked = appState.autoReviewEnabled;
}
async function confirmAutoReviewEnable() {
    const modalElement = document.getElementById('autoReviewModal');
    const agreeButton = document.getElementById('autoReviewAgree');
    const cancelButton = document.getElementById('autoReviewCancel');
    if (!modalElement || !agreeButton || !cancelButton) {
        return window.confirm(t('modals.autoReview.body'));
    }
    const modal = new bootstrap.Modal(modalElement);
    return new Promise(resolve => {
        let resolved = false;
        const finish = (value) => {
            if (resolved)
                return;
            resolved = true;
            agreeButton.removeEventListener('click', onAgree);
            cancelButton.removeEventListener('click', onCancel);
            modalElement.removeEventListener('hidden.bs.modal', onHidden);
            resolve(value);
        };
        const onAgree = () => {
            modal.hide();
            finish(true);
        };
        const onCancel = () => {
            modal.hide();
            finish(false);
        };
        const onHidden = () => {
            finish(false);
        };
        agreeButton.addEventListener('click', onAgree);
        cancelButton.addEventListener('click', onCancel);
        modalElement.addEventListener('hidden.bs.modal', onHidden);
        modal.show();
    });
}
async function handleAutoReviewToggle() {
    if (!dom.autoReviewCheckbox) {
        return;
    }
    // Auto-review works for both logged in and anonymous users
    const user = store.getUser();
    const userId = user?.id ?? 'anonymous';
    const storageKey = `${AUTO_REVIEW_KEY_PREFIX}${userId}`;
    if (!dom.autoReviewCheckbox.checked) {
        appState.autoReviewEnabled = false;
        await storage.save(storageKey, false);
        return;
    }
    const accepted = await confirmAutoReviewEnable();
    if (!accepted) {
        dom.autoReviewCheckbox.checked = false;
        appState.autoReviewEnabled = false;
        return;
    }
    dom.autoReviewCheckbox.checked = true;
    appState.autoReviewEnabled = true;
    await storage.save(storageKey, true);
}
function handleLocaleChange() {
    updateUI();
    renderSettingsModal();
    renderByokKeys();
    renderModelDropdown();
    if (dom.byokProviderSelect) {
        updateByokProviderModels(dom.byokProviderSelect.value);
    }
    // Show demo when no user and no BYOK keys
    if (!store.getUser() && store.getByokKeys().length === 0) {
        restartDemoLoop();
    }
}
function setupElements() {
    initDomElements();
    const deleteConfirmModalElement = document.getElementById('deleteConfirmModal');
    const byokModalElement = document.getElementById('byokModal');
    const settingsModalElement = document.getElementById('settingsModal');
    dom.deleteConfirmModal = deleteConfirmModalElement ? new bootstrap.Modal(deleteConfirmModalElement) : null;
    dom.byokModal = byokModalElement ? new bootstrap.Modal(byokModalElement) : null;
    dom.settingsModal = settingsModalElement ? new bootstrap.Modal(settingsModalElement) : null;
    const confirmSendBtn = document.getElementById('confirmSendBtn');
    const loginBtnTop = document.getElementById('loginBtnTop');
    const loginBtnSidebar = document.getElementById('loginBtnSidebar');
    const loginBtnSidebarMobile = document.getElementById('loginBtnSidebarMobile');
    const logoutBtn = document.getElementById('logoutBtn');
    const logoutBtnMobile = document.getElementById('logoutBtnMobile');
    const upgradeBtn = document.getElementById('upgradeBtn');
    const upgradeBtnMobile = document.getElementById('upgradeBtnMobile');
    const byokBtn = document.getElementById('byokBtn');
    const byokBtnMobile = document.getElementById('byokBtnMobile');
    const settingsBtn = document.getElementById('settingsBtn');
    const settingsBtnMobile = document.getElementById('settingsBtnMobile');
    const upgradeButtons = document.querySelectorAll('[data-upgrade]');
    const socialLoginBtns = document.querySelectorAll('.social-login-btn');
    [loginBtnTop, loginBtnSidebar, loginBtnSidebarMobile].forEach(btn => {
        btn?.addEventListener('click', () => {
            const modal = new bootstrap.Modal(document.getElementById('loginModal'));
            modal.show();
        });
    });
    socialLoginBtns.forEach(btn => {
        btn.addEventListener('click', event => {
            const provider = event.currentTarget.dataset.provider;
            void handleSocialLogin(provider || 'google');
        });
    });
    [logoutBtn, logoutBtnMobile].forEach(btn => {
        btn?.addEventListener('click', event => {
            event.preventDefault();
            void handleLogout().then(() => {
                void syncAutoReviewPreference();
            });
        });
    });
    [upgradeBtn, upgradeBtnMobile].forEach(btn => {
        btn?.addEventListener('click', event => {
            event.preventDefault();
            const modal = new bootstrap.Modal(document.getElementById('paywallModal'));
            modal.show();
        });
    });
    [byokBtn, byokBtnMobile].forEach(btn => {
        btn?.addEventListener('click', event => {
            event.preventDefault();
            renderByokProviders();
            renderByokKeys();
            dom.byokModal?.show();
        });
    });
    [settingsBtn, settingsBtnMobile].forEach(btn => {
        btn?.addEventListener('click', event => {
            event.preventDefault();
            renderSettingsModal();
            dom.settingsModal?.show();
        });
    });
    // Anonymous menu buttons (for users not logged in)
    const byokBtnAnonymous = document.getElementById('byokBtnAnonymous');
    const settingsBtnAnonymous = document.getElementById('settingsBtnAnonymous');
    const byokBtnAnonymousMobile = document.getElementById('byokBtnAnonymousMobile');
    const settingsBtnAnonymousMobile = document.getElementById('settingsBtnAnonymousMobile');
    [byokBtnAnonymous, byokBtnAnonymousMobile].forEach(btn => {
        btn?.addEventListener('click', event => {
            event.preventDefault();
            renderByokProviders();
            renderByokKeys();
            dom.byokModal?.show();
        });
    });
    [settingsBtnAnonymous, settingsBtnAnonymousMobile].forEach(btn => {
        btn?.addEventListener('click', event => {
            event.preventDefault();
            renderSettingsModal();
            dom.settingsModal?.show();
        });
    });
    upgradeButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            handleUpgrade();
        });
    });
    dom.deleteConfirmBtn?.addEventListener('click', () => {
        handleDeleteConfirm();
    });
    dom.byokProviderSelect?.addEventListener('change', () => {
        updateByokProviderModels(dom.byokProviderSelect.value);
    });
    dom.byokAddBtn?.addEventListener('click', () => {
        void handleAddByokKey();
    });
    dom.projectUseGlobalToggle?.addEventListener('change', () => {
        void handleProjectUseGlobalToggle();
    });
    dom.globalSystemPromptInput?.addEventListener('input', () => {
        void handleGlobalPromptChange();
    });
    dom.projectSystemPromptInput?.addEventListener('input', () => {
        void handleProjectPromptChange();
    });
    dom.globalMemoryToggle?.addEventListener('change', () => {
        void handleGlobalMemoryToggle();
    });
    dom.globalMemoryScope?.addEventListener('change', () => {
        void handleGlobalMemoryScopeChange();
    });
    dom.projectMemoryToggle?.addEventListener('change', () => {
        void handleProjectMemoryToggle();
    });
    dom.projectMemoryScope?.addEventListener('change', () => {
        void handleProjectMemoryScopeChange();
    });
    dom.inputText.addEventListener('input', () => {
        dom.inputText.style.height = 'auto';
        dom.inputText.style.height = Math.min(dom.inputText.scrollHeight, 200) + 'px';
        const isReady = appState.isModelLoaded;
        const isLoggedIn = Boolean(store.getUser());
        const hasByokKeys = store.getByokKeys().length > 0;
        const canUseApp = isLoggedIn || hasByokKeys;
        const hasText = dom.inputText.value.trim().length > 0;
        const hasFiles = appState.uploadedFiles.length > 0;
        dom.mainActionBtn.disabled = !isReady || !canUseApp || (!hasText && !hasFiles);
    });
    dom.autoReviewCheckbox?.addEventListener('change', () => {
        void handleAutoReviewToggle();
    });
    const themeToggle = document.getElementById('themeToggle');
    themeToggle?.addEventListener('click', toggleTheme);
    dom.mainActionBtn.addEventListener('click', () => {
        void handleMainAction();
    });
    confirmSendBtn?.addEventListener('click', () => {
        void sendMessageToAI();
    });
    dom.clearBtn.addEventListener('click', handleClear);
    dom.copyBtn.addEventListener('click', handleCopy);
    dom.cancelAnonymizeBtn.addEventListener('click', () => setAppState(AppState.WRITE));
    const openProjectModal = () => {
        // Allow creating projects without login (anonymous mode)
        const modal = new bootstrap.Modal(document.getElementById('projectModal'));
        modal.show();
    };
    document.getElementById('addProjectBtn')?.addEventListener('click', openProjectModal);
    document.getElementById('addProjectBtnMobile')?.addEventListener('click', openProjectModal);
    document.getElementById('saveProjectBtn')?.addEventListener('click', () => {
        // Allow creating projects without login (anonymous mode)
        const nameInput = document.getElementById('projectNameInput');
        if (nameInput.value.trim()) {
            const project = store.addProject(nameInput.value.trim());
            appState.currentProjectId = project.id;
            renderProjects();
            renderChats();
            void refreshProjectSettingsContext();
            const modal = bootstrap.Modal.getInstance(document.getElementById('projectModal'));
            modal?.hide();
            nameInput.value = '';
        }
    });
    const addChat = () => {
        // Allow creating chats without login (anonymous mode)
        if (appState.currentProjectId) {
            const chat = store.addChat(appState.currentProjectId, t('chat.defaultChat', { time: new Date().toLocaleTimeString() }));
            if (chat) {
                appState.currentChatId = chat.id;
                renderChats();
                // Stop demo loop when user creates a new chat (they're actively using the app)
                stopDemoLoop();
                void loadChat();
            }
        }
    };
    document.getElementById('addChatBtn')?.addEventListener('click', addChat);
    document.getElementById('addChatBtnMobile')?.addEventListener('click', addChat);
    dom.composeTab.addEventListener('click', () => {
        closeSidebarOffcanvas();
    });
    initModelDropdown();
    renderByokProviders();
    renderByokKeys();
    const fileInput = document.getElementById('fileInput');
    const browseBtn = document.getElementById('browseBtn');
    const fileDropZone = document.getElementById('fileDropZone');
    const clearAllFilesBtn = document.getElementById('clearAllFilesBtn');
    browseBtn.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', handleFileSelect);
    clearAllFilesBtn.addEventListener('click', clearAllFiles);
    const composeContainer = document.querySelector('.input-compose-container');
    composeContainer.addEventListener('dragover', event => {
        event.preventDefault();
        fileDropZone.classList.add('drag-over');
    });
    composeContainer.addEventListener('dragleave', event => {
        if (event.relatedTarget === null || !(composeContainer.contains(event.relatedTarget))) {
            fileDropZone.classList.remove('drag-over');
        }
    });
    composeContainer.addEventListener('drop', event => {
        event.preventDefault();
        fileDropZone.classList.remove('drag-over');
        void handleDrop(event);
    });
    registerDeleteHandlers({
        onProjectDelete: projectId => {
            void deleteProjectMemory(projectId);
            store.deleteProject(projectId);
            clearProjectSettings(projectId);
            resetProjectSelection();
            renderProjects();
            renderChats();
            void loadChat();
            void refreshProjectSettingsContext();
        },
        onChatDelete: (projectId, chatId) => {
            void deleteChatMemory(projectId, chatId);
            store.deleteChat(projectId, chatId);
            resetProjectSelection();
            renderProjects();
            renderChats();
            void loadChat();
        },
        onByokDelete: keyId => {
            store.removeByokKey(keyId);
            renderByokKeys();
            renderModelDropdown();
            updateByokProviderModels(dom.byokProviderSelect.value);
            // Update UI to hide BYOK toolbar buttons if no keys left
            updateUI();
        }
    });
}
async function bootstrapApp() {
    initializeI18n();
    setupElements();
    initializeLanguageSelect();
    document.addEventListener('bq:locale-change', handleLocaleChange);
    await loadGlobalSettings();
    renderSettingsModal();
    void initializePipeline();
    const authenticatedUser = await bootstrapAuthSession();
    if (!authenticatedUser) {
        // Initialize encryption for anonymous users so they can use BYOK
        await initializeEncryptionForUser(null);
        await store.initialize();
    }
    resetProjectSelection();
    renderProjects();
    renderChats();
    await loadChat();
    await refreshProjectSettingsContext();
    await syncAutoReviewPreference();
    updateUI();
    renderByokKeys();
    await loadByokModelCatalog();
    renderModelDropdown();
}
document.addEventListener('DOMContentLoaded', () => {
    void bootstrapApp();
});
